import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import SyncIcon from "@mui/icons-material/Sync";
import { ProductItem } from "./types/products";
import { useTransitionProductItem } from "../../hooks/api/useProductItem";
import {
  useKivunovaTranslation,
  BRAND_COLOR,
  BRAND_HOVER
} from "@kivunova/kivufrontendcommon";
import { formatProductItemStatus } from "./utils/productUtils";
import FormModal, { formFieldSx } from "../common/FormModal/FormModal";
import {
  guidanceOutlinedButtonSx,
  guidancePrimaryButtonSx
} from "../common/GuidanceDialog/guidanceDialogStyles";

interface TransitionProductItemModalProps {
  open: boolean;
  onClose: () => void;
  item: ProductItem;
  onSuccess?: () => void;
}

const TransitionProductItemModal: React.FC<TransitionProductItemModalProps> = ({
  open,
  onClose,
  item,
  onSuccess
}) => {
  const { t } = useKivunovaTranslation();
  const transitionMutation = useTransitionProductItem();
  const [event, setEvent] = useState("");
  const [quantity, setQuantity] = useState<number>(1);
  const [fulfillingReservation, setFulfillingReservation] = useState(false);

  useEffect(() => {
    if (!open) {
      setEvent("");
      setQuantity(1);
      setFulfillingReservation(false);
    }
  }, [open]);

  const availableEvents = [
    { value: "STOCK", label: t("event-stock", "Stock") },
    { value: "RESERVE", label: t("event-reserve", "Reserve") },
    {
      value: "CANCEL_RESERVATION",
      label: t("event-cancel-reservation", "Cancel Reservation")
    },
    {
      value: "COMPLETE_SALE",
      label: t("event-complete-sale", "Complete Sale")
    },
    { value: "MARK_DAMAGED", label: t("event-mark-damaged", "Mark Damaged") },
    { value: "MARK_LOST", label: t("event-mark-lost", "Mark Lost") },
    { value: "MARK_EXPIRED", label: t("event-mark-expired", "Mark Expired") },
    { value: "MARK_CONSUMED", label: t("event-mark-consumed", "Mark Consumed") }
  ];

  const handleTransition = async () => {
    if (!event || quantity <= 0) return;

    try {
      await transitionMutation.mutateAsync({
        productItemId: item.id,
        event,
        quantity,
        fulfillingReservation:
          event === "COMPLETE_SALE" ? fulfillingReservation : undefined
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Transition error:", error);
    }
  };

  const busy = transitionMutation.isPending;

  return (
    <FormModal
      open={open}
      onClose={onClose}
      title={t("transition-status-title", "Change status")}
      subtitle={`${t("item-id", "Item ID")}: ${item.id} · ${t("current-status", "Current Status")}: ${formatProductItemStatus(item.state, t)}`}
      icon={<SyncIcon sx={{ fontSize: 20 }} />}
      maxWidth="sm"
      dataTestId="transition-product-item-modal"
      footer={
        <>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={busy}
            sx={{ ...guidanceOutlinedButtonSx, minWidth: 120 }}
          >
            {t("cancel", "Cancel")}
          </Button>
          <LoadingButton
            variant="contained"
            onClick={handleTransition}
            loading={busy}
            disabled={!event || quantity <= 0}
            sx={{
              ...guidancePrimaryButtonSx,
              minWidth: 120,
              backgroundColor: BRAND_COLOR,
              "&:hover": { backgroundColor: BRAND_HOVER }
            }}
          >
            {t("transition", "Transition")}
          </LoadingButton>
        </>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <FormControl fullWidth size="small" sx={formFieldSx}>
          <InputLabel id="transition-event-label">
            {t("select-event", "Select Event")}
          </InputLabel>
          <Select
            labelId="transition-event-label"
            value={event}
            label={t("select-event", "Select Event")}
            onChange={(e) => {
              setEvent(e.target.value);
              if (e.target.value !== "COMPLETE_SALE") {
                setFulfillingReservation(false);
              }
            }}
          >
            {availableEvents.map((evt) => (
              <MenuItem key={evt.value} value={evt.value}>
                {evt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label={t("quantity", "Quantity")}
          type="number"
          size="small"
          value={quantity}
          onChange={(e) => setQuantity(Number(e.target.value))}
          inputProps={{ min: 1, max: item.remainingQuantity }}
          helperText={`${t("available-quantity", "Available Quantity")}: ${item.remainingQuantity}`}
          sx={formFieldSx}
        />

        {event === "COMPLETE_SALE" && (
          <FormControl fullWidth size="small" sx={formFieldSx}>
            <InputLabel id="fulfilling-reservation-label">
              {t("fulfilling-reservation", "Fulfilling Reservation?")}
            </InputLabel>
            <Select
              labelId="fulfilling-reservation-label"
              value={fulfillingReservation ? "yes" : "no"}
              label={t("fulfilling-reservation", "Fulfilling Reservation?")}
              onChange={(e) =>
                setFulfillingReservation(e.target.value === "yes")
              }
            >
              <MenuItem value="no">{t("no", "No")}</MenuItem>
              <MenuItem value="yes">{t("yes", "Yes")}</MenuItem>
            </Select>
          </FormControl>
        )}

        {transitionMutation.isError && (
          <Alert severity="error">
            {t(
              "transition-error",
              "Failed to transition status. Please check your permissions and try again."
            )}
          </Alert>
        )}
      </Box>
    </FormModal>
  );
};

export default TransitionProductItemModal;
